import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'modules/Common/utils/cn';

/**
 * Button component styled to match the Radix UI / Tailwind design language
 * used throughout CgDialog, CgSearchFilter, and FormActions.
 *
 * Differences from CgButton (the Bootstrap-era button):
 *  - rounded-xl instead of rounded-lg
 *  - No box-shadow by default
 *  - focus-visible ring instead of focus outline
 *  - Lighter, flatter variants that sit well inside dialog footers
 *
 * Use this anywhere a button lives inside or alongside a CgDialog, Radix
 * popover, or the v2 Form system. Use CgButton for legacy Bootstrap layouts.
 *
 * Variants and what they MEAN — `primary` is the tenant's BRAND (`bg-primaryColor`,
 * set at runtime by whitelabel branding: mint on Carbon Glance, purple on
 * Faradai), so it says "this product", never "this state". `success` is the
 * state-green — the transition that changes a record's standing (Finalize).
 * `dark` is the house dark (slate-900: the auditor's active tab, the plan rail)
 * for the one terminal action on a bar. Eleven raw emerald buttons imitated the
 * primitive's shape before `success` existed; they were the variant that had
 * never been written down.
 *
 * @param {'primary'|'secondary'|'ghost'|'outline'|'danger'|'success'|'dark'} [variant='secondary']
 * @param {'sm'|'md'|'lg'} [size='md']
 * @param {object}  [icon]      FontAwesome icon definition shown before the label.
 * @param {string}  [loadingLabel]  Label shown while loading (defaults to children).
 * @param {boolean} [loading=false]
 * @param {boolean} [disabled=false]
 * @param {'button'|'submit'|'reset'} [type='button']
 */
const CgRadixButton = React.forwardRef(
  (
    {
      variant = 'secondary',
      size = 'md',
      icon,
      iconPosition = 'start',
      children,
      label,
      loadingLabel,
      loading = false,
      disabled = false,
      type = 'button',
      onClick,
      className = '',
      ...props
    },
    ref,
  ) => {
    const base =
      'inline-flex items-center gap-1.5 font-medium rounded-xl transition-colors ' +
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 ' +
      'disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      primary: 'bg-primaryColor text-white hover:bg-primaryColor/90',
      secondary: 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200',
      ghost: 'text-slate-500 hover:bg-slate-100',
      outline: 'text-primaryColor border border-primaryColor/30 hover:bg-primaryColor/5',
      danger: 'bg-red-500 text-white hover:bg-red-600',
      success: 'bg-emerald-600 text-white hover:bg-emerald-700',
      dark: 'bg-slate-900 text-white hover:bg-slate-800',
    };

    const sizes = {
      sm: 'text-xs px-2.5 py-1',
      md: 'text-sm px-3 py-1.5',
      lg: 'text-sm px-4 py-2',
    };

    const spinner = (
      <svg
        className="animate-spin w-3.5 h-3.5 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
    );

    const content = children ?? label;
    const iconNode = icon ? (
      <FontAwesomeIcon
        icon={icon}
        className="w-3.5 h-3.5 shrink-0"
      />
    ) : null;

    return (
      <button
        ref={ref}
        type={type}
        onClick={loading ? undefined : onClick}
        disabled={disabled || loading}
        className={cn(base, variants[variant], sizes[size], className)}
        {...props}
      >
        {loading ? (
          <>
            {spinner}
            {loadingLabel ?? content}
          </>
        ) : (
          <>
            {iconPosition === 'end' ? null : iconNode}
            {content}
            {iconPosition === 'end' ? iconNode : null}
          </>
        )}
      </button>
    );
  },
);

CgRadixButton.displayName = 'CgRadixButton';

export default CgRadixButton;
